import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

const MIDDLEWARE = process.env.REACT_APP_API_URL;


// ── Design tokens (LOGIMES system) ─────────────────────────────────────────
const s = {
  page:    { minHeight: "100vh", background: "#f0efff", fontFamily: "'Outfit', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px" },
  card:    { background: "#fff", borderRadius: 20, boxShadow: "0 8px 40px rgba(108,99,255,0.10)", padding: "48px 44px", width: "100%", maxWidth: 520 },
  // Progress
  progress:     { display: "flex", alignItems: "center", marginBottom: 40, gap: 0 },
  stepDot:      (active, done) => ({ width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0, transition: "all .3s", background: done ? "#6c63ff" : active ? "#6c63ff" : "#f0efff", color: done || active ? "#fff" : "#aaa", border: done || active ? "none" : "2px solid #e2e8f0" }),
  stepLine:     (done) => ({ flex: 1, height: 3, background: done ? "#6c63ff" : "#e2e8f0", transition: "background .4s", margin: "0 4px" }),
  stepLabel:    (active, done) => ({ fontSize: 11, fontWeight: 600, color: done ? "#6c63ff" : active ? "#6c63ff" : "#aaa", marginTop: 6, textAlign: "center", whiteSpace: "nowrap" }),
  stepCol:      { display: "flex", flexDirection: "column", alignItems: "center" },
  // Titles
  h1:      { fontSize: 24, fontWeight: 800, color: "#1a1a2e", marginBottom: 6 },
  sub:     { fontSize: 14, color: "#7a7a9a", marginBottom: 32, lineHeight: 1.6 },
  // Form
  label:   { fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, display: "block" },
  input:   { width: "100%", padding: "12px 16px", border: "2px solid #e2e8f0", borderRadius: 10, fontSize: 14, fontFamily: "'Outfit', sans-serif", outline: "none", boxSizing: "border-box", transition: "border .2s", background: "#fafafa" },
  inputFocus: { border: "2px solid #6c63ff" },
  group:   { marginBottom: 20 },
  // Buttons
  btnPrimary: { width: "100%", padding: "14px", background: "linear-gradient(135deg,#6c63ff,#a78bfa)", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "opacity .2s, transform .1s", marginTop: 8 },
  btnSecondary: { width: "100%", padding: "13px", background: "#f0efff", color: "#6c63ff", border: "2px solid #e0ddff", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit', sans-serif", marginTop: 8 },
  // Alerts
  success: { background: "#f0fff4", border: "1.5px solid #6ee7b7", borderRadius: 10, padding: "12px 16px", color: "#065f46", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
  error:   { background: "#fff1f2", border: "1.5px solid #fca5a5", borderRadius: 10, padding: "12px 16px", color: "#991b1b", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
  // File upload
  dropzone: (drag) => ({ border: `2px dashed ${drag ? "#6c63ff" : "#c7c3ff"}`, borderRadius: 12, padding: "28px 20px", textAlign: "center", cursor: "pointer", background: drag ? "#f5f3ff" : "#faf9ff", transition: "all .2s" }),
  // Recap
  recapRow: { display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f0efff", fontSize: 14 },
  badge:    (color) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: color === "green" ? "#d1fae5" : color === "orange" ? "#fef3c7" : "#ede9fe", color: color === "green" ? "#065f46" : color === "orange" ? "#92400e" : "#5b21b6" }),
};

// ── Step indicator ──────────────────────────────────────────────────────────
const STEPS = ["Informations", "Certificat ANCE", "Confirmation"];

function StepBar({ current }) {
  return (
    <div style={s.progress}>
      {STEPS.map((label, i) => (
        <div key={i} style={{ display: "flex", alignItems: "flex-start", flex: i < STEPS.length - 1 ? 1 : undefined }}>
          <div style={s.stepCol}>
            <div style={s.stepDot(current === i, current > i)}>
              {current > i ? "✓" : i + 1}
            </div>
            <span style={s.stepLabel(current === i, current > i)}>{label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, paddingTop: 18 }}>
              <div style={s.stepLine(current > i)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Matricule fiscal validator ──────────────────────────────────────────────
// Format officiel Tunisie: 7 chiffres + lettre + /X/X/XXX
// Ex: 1234567A/P/M/000  ou  1234567B/N/P/001
const MF_REGEX = /^\d{7}[A-Z]\/[A-Z]\/[A-Z]\/\d{3}$/;

function validateMF(val) {
  if (!val) return { ok: false, msg: "Matricule fiscal obligatoire" };
  const clean = val.trim().toUpperCase();
  if (!MF_REGEX.test(clean)) {
    return {
      ok: false,
      msg: "Format invalide — attendu: 1234567A/P/M/000"
    };
  }
  return { ok: true, msg: "✓ Matricule fiscal valide" };
}

// ── Matricule fiscal input ──────────────────────────────────────────────────
function MatriculeFiscalField({ value, onChange }) {
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);

  const validation = validateMF(value);
  const showError  = touched && !validation.ok;
  const showOk     = touched && validation.ok;

  const borderColor = showError ? "#f87171"
                    : showOk   ? "#34d399"
                    : focused  ? "#6c63ff"
                    : "#e2e8f0";

  // Auto-format: insère les "/" au bon endroit pendant la frappe
  const handleChange = (e) => {
    let v = e.target.value.toUpperCase().replace(/[^A-Z0-9\/]/g, "");
    onChange(v);
  };

  return (
    <div style={s.group}>
      <label style={s.label}>🔢 Matricule fiscal *</label>

      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); setTouched(true); }}
          placeholder="Ex: 1234567A/P/M/000"
          maxLength={16}
          style={{
            ...s.input,
            border: `2px solid ${borderColor}`,
            paddingRight: 36,
            fontFamily: "monospace",
            fontSize: 15,
            letterSpacing: 1,
          }}
        />
        {/* Icône statut */}
        {touched && (
          <span style={{
            position: "absolute", right: 12, top: "50%",
            transform: "translateY(-50%)", fontSize: 16
          }}>
            {validation.ok ? "✅" : "❌"}
          </span>
        )}
      </div>

      {/* Message feedback */}
      {showError && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626", display: "flex", alignItems: "center", gap: 4 }}>
          ⚠️ {validation.msg}
        </div>
      )}
      {showOk && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#059669", display: "flex", alignItems: "center", gap: 4 }}>
          {validation.msg}
        </div>
      )}

      {/* Guide format */}
      {focused && !showOk && (
        <div style={{ marginTop: 8, padding: "10px 12px", background: "#f5f3ff", borderRadius: 8, border: "1px solid #e0ddff" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6c63ff", marginBottom: 6 }}>📋 Format matricule fiscal tunisien</div>
          <div style={{ fontFamily: "monospace", fontSize: 13, color: "#374151", letterSpacing: 1 }}>
            <span style={{ background: "#ddd6fe", padding: "2px 4px", borderRadius: 3 }}>1234567</span>
            <span style={{ background: "#fde68a", padding: "2px 4px", borderRadius: 3 }}>A</span>
            <span style={{ color: "#aaa" }}>/</span>
            <span style={{ background: "#bbf7d0", padding: "2px 4px", borderRadius: 3 }}>P</span>
            <span style={{ color: "#aaa" }}>/</span>
            <span style={{ background: "#bfdbfe", padding: "2px 4px", borderRadius: 3 }}>M</span>
            <span style={{ color: "#aaa" }}>/</span>
            <span style={{ background: "#fecaca", padding: "2px 4px", borderRadius: 3 }}>000</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#6b7280", lineHeight: 1.6 }}>
            <span style={{ background: "#ddd6fe", padding: "1px 4px", borderRadius: 2 }}>7 chiffres</span>{" "}
            <span style={{ background: "#fde68a", padding: "1px 4px", borderRadius: 2 }}>Lettre clé</span>{" "}
            <span style={{ background: "#bbf7d0", padding: "1px 4px", borderRadius: 2 }}>Type (P/N)</span>{" "}
            <span style={{ background: "#bfdbfe", padding: "1px 4px", borderRadius: 2 }}>Catégorie</span>{" "}
            <span style={{ background: "#fecaca", padding: "1px 4px", borderRadius: 2 }}>Établissement</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Input helper ────────────────────────────────────────────────────────────
function Field({ label, icon, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={s.group}>
      <label style={s.label}>{icon} {label}</label>
      <input
        {...props}
        style={{ ...s.input, ...(focused ? s.inputFocus : {}) }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STEP 1 — Informations société
// ═══════════════════════════════════════════════════════════════════════════
function Step1({ data, setData, onNext, loading, error }) {
  const [logoPreview, setLogoPreview] = useState(null);
  const logoRef = useRef();

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setData({ ...data, logo: file, logoName: file.name });
    setLogoPreview(URL.createObjectURL(file));
  };

  return (
    <div>
      <div style={s.h1}>🏢 Informations société</div>
      <div style={s.sub}>Renseignez les informations de la nouvelle société à intégrer dans LOGIMES TEIF.</div>

      {error && <div style={s.error}>⚠️ {error}</div>}

      {/* Logo upload */}
      <div style={s.group}>
        <label style={s.label}>🖼️ Logo de la société</label>
        <div
          onClick={() => logoRef.current.click()}
          style={{ width: 100, height: 100, borderRadius: 16, border: "2px dashed #c7c3ff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "#faf9ff", overflow: "hidden", marginBottom: 8 }}
        >
          {logoPreview ? (
            <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 36 }}>🏢</span>
          )}
        </div>
        <input ref={logoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogo} />
        {data.logoName && <div style={{ fontSize: 12, color: "#6c63ff" }}>📎 {data.logoName}</div>}
      </div>

      <Field label="Nom de la société *" icon="🏷️" type="text" placeholder="Ex: LOGIMES SARL"
        value={data.name} onChange={e => setData({ ...data, name: e.target.value })} />
      <MatriculeFiscalField
        value={data.tax_id}
        onChange={val => setData({ ...data, tax_id: val })}
      />
      <Field label="Adresse" icon="📍" type="text" placeholder="Ex: 12 Rue de la République, Tunis"
        value={data.address} onChange={e => setData({ ...data, address: e.target.value })} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={s.group}>
          <label style={s.label}>📞 Téléphone</label>
          <input style={s.input} type="tel" placeholder="+216 xx xxx xxx"
            value={data.phone} onChange={e => setData({ ...data, phone: e.target.value })} />
        </div>
        <div style={s.group}>
          <label style={s.label}>📧 Email</label>
          <input style={s.input} type="email" placeholder="contact@societe.tn"
            value={data.email} onChange={e => setData({ ...data, email: e.target.value })} />
        </div>
      </div>

      <button style={{ ...s.btnPrimary, opacity: loading ? 0.7 : 1 }}
        onClick={onNext} disabled={loading}>
        {loading ? "⏳ Création en cours..." : "Continuer →"}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STEP 2 — Certificat ANCE
// ═══════════════════════════════════════════════════════════════════════════
function Step2({ companyId, certData, setCertData, onNext, onBack, loading, error, success }) {
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    setCertData({ ...certData, file, filename: file.name });
  };

  return (
    <div>
      <div style={s.h1}>🔐 Certificat ANCE</div>
      <div style={s.sub}>Uploadez le certificat électronique de la société pour signer les factures TEIF.</div>

      {error   && <div style={s.error}>⚠️ {error}</div>}
      {success && <div style={s.success}>✅ {success}</div>}

      {/* Dropzone */}
      <div style={s.group}>
        <label style={s.label}>📄 Fichier certificat (.p12 ou .pem) *</label>
        <div
          style={s.dropzone(drag)}
          onClick={() => fileRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        >
          {certData.file ? (
            <div>
              <div style={{ fontSize: 32 }}>📋</div>
              <div style={{ fontWeight: 700, color: "#6c63ff", marginTop: 8 }}>{certData.filename}</div>
              <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
                {(certData.file.size / 1024).toFixed(1)} KB — Cliquer pour changer
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 36 }}>📁</div>
              <div style={{ fontWeight: 600, color: "#6c63ff", marginTop: 8 }}>Glisser-déposer ou cliquer</div>
              <div style={{ color: "#aaa", fontSize: 12, marginTop: 4 }}>Format .p12 ou .pem accepté</div>
            </div>
          )}
          <input ref={fileRef} type="file" accept=".p12,.pem,.pfx" style={{ display: "none" }}
            onChange={e => handleFile(e.target.files[0])} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={s.group}>
          <label style={s.label}>🔑 Mot de passe</label>
          <input style={s.input} type="password" placeholder="••••••••••"
            value={certData.password} onChange={e => setCertData({ ...certData, password: e.target.value })} />
        </div>
        <div style={s.group}>
          <label style={s.label}>📅 Date d'expiration</label>
          <input style={s.input} type="date"
            value={certData.expires_at} onChange={e => setCertData({ ...certData, expires_at: e.target.value })} />
        </div>
      </div>

      <div style={s.group}>
        <label style={s.label}>📂 Type de certificat</label>
        <select style={s.input} value={certData.cert_type}
          onChange={e => setCertData({ ...certData, cert_type: e.target.value })}>
          <option value="PEM">PEM — Développement / Test</option>
          <option value="P12">P12 / PKCS#12 — Production ANCE</option>
          <option value="PFX">PFX — Windows / IIS</option>
        </select>
      </div>

      <button style={{ ...s.btnPrimary, opacity: loading ? 0.7 : 1 }}
        onClick={onNext} disabled={loading}>
        {loading ? "⏳ Upload en cours..." : "✅ Uploader et Tester"}
      </button>
      <button style={s.btnSecondary} onClick={onBack}>← Retour</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STEP 3 — Confirmation & Récap
// ═══════════════════════════════════════════════════════════════════════════
function Step3({ company, certData, onFinish, onReset }) {
  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 56 }}>🎉</div>
        <div style={{ ...s.h1, textAlign: "center", marginTop: 8 }}>Société configurée !</div>
        <div style={{ ...s.sub, textAlign: "center" }}>
          La société a été intégrée avec succès dans LOGIMES TEIF.
        </div>
      </div>

      {/* Recap card */}
      <div style={{ background: "#faf9ff", borderRadius: 14, padding: "20px 24px", marginBottom: 24, border: "1.5px solid #e0ddff" }}>
        <div style={{ fontWeight: 700, color: "#6c63ff", marginBottom: 12, fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>
          📋 Récapitulatif
        </div>

        <div style={s.recapRow}>
          <span style={{ color: "#888" }}>Société</span>
          <span style={{ fontWeight: 600 }}>{company?.name}</span>
        </div>
        <div style={s.recapRow}>
          <span style={{ color: "#888" }}>Matricule fiscal</span>
          <span style={{ fontWeight: 600, fontFamily: "monospace" }}>{company?.tax_id}</span>
        </div>
        {(company?.address || company?.adresse) && (
          <div style={s.recapRow}>
            <span style={{ color: "#888" }}>Adresse</span>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{company.address}</span>
          </div>
        )}
        <div style={s.recapRow}>
          <span style={{ color: "#888" }}>Certificat</span>
          <span style={s.badge("green")}>✓ {certData.cert_type} — Actif</span>
        </div>
        {certData.expires_at && (
          <div style={s.recapRow}>
            <span style={{ color: "#888" }}>Expiration cert.</span>
            <span style={s.badge("orange")}>📅 {certData.expires_at}</span>
          </div>
        )}
        <div style={{ ...s.recapRow, borderBottom: "none", paddingBottom: 0 }}>
          <span style={{ color: "#888" }}>Statut</span>
          <span style={s.badge("purple")}>⚡ Prête à facturer</span>
        </div>
      </div>

            {/* Actions */}
      <button style={{ ...s.btnPrimary, marginTop: 0, marginBottom: 12 }} onClick={onFinish}>
        🏠 Tableau de bord
      </button>
      <button style={{ ...s.btnSecondary, marginTop: 0 }} onClick={onReset}>
        ➕ Autre société
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function CompanyOnboarding({ onComplete, editMode = false }) {
  const navigate = useNavigate();
  const [step,     setStep]     = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  // Step 1 data
  const [companyData, setCompanyData] = useState({ name: "", tax_id: "", address: "", phone: "", email: "" });
  const [createdCompany, setCreatedCompany] = useState(null);
  // Charger les données existantes si editMode
useEffect(() => {
  if (!editMode) return;
  const id = getCookie("company_id");
  if (!id) return;
  fetch(`${MIDDLEWARE}/companies/${id}`)
    .then(r => r.json())
    .then(d => {
      setCompanyData({ name: d.name || "", tax_id: d.tax_id || "", address: d.address || "", phone: d.phone || "", email: d.email || "" });
      setCreatedCompany(d);
      setStep(0);
    }).catch(() => {});
}, [editMode]);

  // Step 2 data
  const [certData, setCertData] = useState({ file: null, filename: "", password: "", expires_at: "", cert_type: "PEM" });

    // ── Step 1 submit ──────────────────────────────────────────────────────
  //
  // Vérifie d'abord si la société existe dans DAS ERP (via /societes/check).
  // Si oui (avec données) → rattache directement au societe_id DAS ERP.
  // Sinon → crée une société standalone (PostgreSQL uniquement, espace vierge).
  //
  const handleCreateCompany = async () => {
    setError("");
    if (!companyData.name.trim()) return setError("Le nom de la société est obligatoire.");
    const mfCheck = validateMF(companyData.tax_id);
    if (!mfCheck.ok) return setError("Matricule fiscal invalide — " + mfCheck.msg);

    setLoading(true);
    try {
      const nomClean = companyData.name.trim().toUpperCase();
      const token    = getCookie("token");

      // ── 1. Vérification DAS ERP (proxy Bridge) ────────────────
      const checkRes  = await fetch(
        `${MIDDLEWARE}/societes/check?nom=${encodeURIComponent(nomClean)}`,
        { headers: token ? { "Authorization": `Bearer ${token}` } : {} }
      );
      const checkData = await checkRes.json();

      let societeId, clientType, data;

      if (checkData.exists && checkData.has_data) {
        // ── Société trouvée dans DAS ERP avec données réelles ──
        societeId  = checkData.societe_id;
        clientType = "erp";
        data = {
          company_id: societeId, id: societeId,
          name: companyData.name.trim(), tax_id: companyData.tax_id,
          address: companyData.address,
          societe_id: societeId, societe_folder: nomClean,
          scenario: "A",
        };
      } else {
        // ── Absente / sans données DAS ERP → création standalone ──
        const companyParams = new URLSearchParams({
          name:    companyData.name.trim(),
          tax_id:  companyData.tax_id  || "",
          address: companyData.address || "",
          phone:   companyData.phone   || "",
          email:   companyData.email   || "",
        });
        const createRes = await fetch(`${MIDDLEWARE}/companies?${companyParams}`, {
          method:  "POST",
          headers: token ? { "Authorization": `Bearer ${token}` } : {},
        });
        if (!createRes.ok) throw new Error("Erreur lors de la création de la société en base.");
        const createData = await createRes.json();
        societeId  = createData.id || createData.company_id || createData.societe_id;
        clientType = "standalone";
        data = {
          company_id: societeId, id: societeId,
          name: companyData.name.trim(), tax_id: companyData.tax_id,
          address: companyData.address,
          societe_id: societeId, societe_folder: nomClean,
          scenario: "B",
        };
      }

      // ── Save cookies ──────────────────────────────
      document.cookie = `societe_folder=${nomClean};path=/;SameSite=Strict;Max-Age=86400`;
      document.cookie = `societe=${companyData.name.trim()};path=/;SameSite=Strict;Max-Age=86400`;
      document.cookie = `client_type=${clientType};path=/;SameSite=Strict;Max-Age=2592000`;
      document.cookie = `societe_id=${societeId};path=/;SameSite=Strict;Max-Age=86400`;

      setCreatedCompany(data);
      setStep(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 submit ──────────────────────────────────────────────────────
 const handleUploadCert = async () => {
  setError(""); setSuccess("");
  if (!certData.filename) return setError("Veuillez sélectionner un fichier certificat.");
  setLoading(true);
  try {
    const societeId = createdCompany?.societe_id || createdCompany?.company_id || createdCompany?.id;
    localStorage.setItem(`cert_${societeId}`, JSON.stringify({
      filename:   certData.filename,
      cert_type:  certData.cert_type,
      expires_at: certData.expires_at,
      uploaded:   new Date().toISOString()
    }));
    setSuccess("Certificat ajouté avec succès !");
    setTimeout(() => setStep(2), 900);
  } catch (e) { setError(e.message); }
  finally { setLoading(false); }
};
  // ── Nouvelle facture (pour cette société) ────────────────────────────────
  const handleNewFacture = () => {
    const societeId = createdCompany?.societe_id || createdCompany?.company_id || createdCompany?.id;
    if (societeId) {
      document.cookie = `societe_id=${societeId};path=/;SameSite=Strict;Max-Age=86400`;
    }
    navigate("/facture/create");
  };

  // ── Reset ──────────────────────────────────────────────────────────────
  const handleReset = () => {
    setStep(0); setError(""); setSuccess("");
    setCompanyData({ name: "", tax_id: "", address: "", phone: "", email: "" });
    setCertData({ file: null, filename: "", password: "", expires_at: "", cert_type: "PEM" });
    setCreatedCompany(null);
  };

const handleFinish = async () => {
  try {
    const token = getCookie("token");

    // ── 1. La société est déjà créée en PostgreSQL depuis l'étape 1 ──
    // (plus aucune lecture depuis DAS ERP)
    const societeId = createdCompany?.societe_id || createdCompany?.company_id || createdCompany?.id;

    // ── 2. Save cookie ────────────────────────────
    if (societeId) {
      document.cookie = `societe_id=${societeId};path=/;SameSite=Strict;Max-Age=86400`;
    }

    // ── 3. La société a déjà été créée en PostgreSQL à l'étape 1,
    // pas besoin de la recréer ici (on évite un doublon).

        // ── 4. Update user fil backend ────────────────
    if (token && societeId) {
      const clientType = getCookie("client_type") || "standalone";
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/update-societe`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ societe_id: societeId, client_type: clientType })
      });
    }

    // ── 5. Logo ───────────────────────────────────
    if (companyData.logo && societeId) {
      const reader = new FileReader();
      reader.onload = (e) => {
        localStorage.setItem(`logo_${societeId}`, e.target.result);
      };
      reader.readAsDataURL(companyData.logo);
    }

  } catch (e) {
    console.error("Erreur handleFinish:", e);
  }

  if (onComplete) onComplete(createdCompany);
  else window.location.href = "/home";
};

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <div style={s.page}>
        <div style={s.card}>
          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 22 }}>🏢</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#6c63ff", textTransform: "uppercase", letterSpacing: 2 }}>
                LOGIMES TEIF
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#aaa", fontWeight: 500 }}>Intégration nouvelle société</div>
          </div>

          {/* Step bar */}
          <StepBar current={step} />

          {/* Steps */}
          {step === 0 && (
            <Step1
              data={companyData} setData={setCompanyData}
              onNext={handleCreateCompany}
              loading={loading} error={error}
            />
          )}
          {step === 1 && (
            <Step2
              companyId={createdCompany?.company_id}
              certData={certData} setCertData={setCertData}
              onNext={handleUploadCert}
              onBack={() => { setStep(0); setError(""); }}
              loading={loading} error={error} success={success}
            />
          )}
          {step === 2 && (
            <Step3
              company={{ ...companyData, ...createdCompany }}
              certData={certData}
              onFinish={handleFinish}
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </>
  );
}